import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, Globe, TrendingUp, Users, Clock, BarChart } from 'lucide-react';
import { SEO } from '../components/SEO';

interface WebsiteValueState {
  monthlyRevenue: string;
  monthlyProfit: string;
  monthlyVisitors: string;
  websiteAge: string;
  industry: 'ecommerce' | 'blog' | 'saas' | 'media' | 'services' | 'other';
  growthRate: 'declining' | 'stable' | 'growing' | 'rapid';
  hasCustomDomain: boolean;
  hasSocialPresence: boolean;
  hasEmailList: boolean;
}

interface WebsiteValueResult {
  revenueMultiplier: number;
  profitMultiplier: number;
  trafficValue: number;
  ageValue: number;
  industryFactor: number;
  growthFactor: number;
  assetsFactor: number;
  estimatedValue: number;
  valueRange: {
    min: number;
    max: number;
  };
  monthlyMultiple: number;
}

function KalkulatorVrijednostiStranice() {
  const [values, setValues] = useState<WebsiteValueState>({
    monthlyRevenue: '',
    monthlyProfit: '',
    monthlyVisitors: '',
    websiteAge: '',
    industry: 'ecommerce',
    growthRate: 'stable',
    hasCustomDomain: true,
    hasSocialPresence: false,
    hasEmailList: false
  });

  const [results, setResults] = useState<WebsiteValueResult | null>(null);

  const industryMultipliers = {
    ecommerce: { min: 24, max: 36 },
    blog: { min: 24, max: 36 },
    saas: { min: 36, max: 48 },
    media: { min: 24, max: 36 },
    services: { min: 24, max: 36 },
    other: { min: 18, max: 30 }
  };

  const growthFactors = {
    declining: 0.7,
    stable: 1.0,
    growing: 1.3,
    rapid: 1.6
  };

  const calculateWebsiteValue = () => {
    const monthlyRevenue = parseFloat(values.monthlyRevenue) || 0;
    const monthlyProfit = parseFloat(values.monthlyProfit) || 0;
    const monthlyVisitors = parseFloat(values.monthlyVisitors) || 0;
    const websiteAge = parseFloat(values.websiteAge) || 0;
    
    if (monthlyRevenue === 0 && monthlyProfit === 0 && monthlyVisitors === 0) {
      return null;
    }

    // Calculate base multipliers
    const industryMultiplier = industryMultipliers[values.industry];
    const revenueMultiplier = (industryMultiplier.min + industryMultiplier.max) / 2;
    const profitMultiplier = revenueMultiplier * 1.2; // Profit is typically valued higher than revenue
    
    // Calculate traffic value (€0.5-2 per visitor depending on industry)
    let visitorValue = 0.5;
    if (['ecommerce', 'saas'].includes(values.industry)) {
      visitorValue = 2;
    } else if (['blog', 'media'].includes(values.industry)) {
      visitorValue = 1;
    }
    const trafficValue = monthlyVisitors * visitorValue * 12; // Annual value
    
    // Calculate age value (older sites are more valuable)
    const ageValue = Math.min(websiteAge * 500, 5000); // Up to €5000 for age
    
    // Calculate industry factor
    const industryFactor = values.industry === 'saas' ? 1.5 : 
                          (values.industry === 'ecommerce' ? 1.3 : 1.0);
    
    // Calculate growth factor
    const growthFactor = growthFactors[values.growthRate];
    
    // Calculate assets factor
    let assetsFactor = 1.0;
    if (values.hasCustomDomain) assetsFactor += 0.1;
    if (values.hasSocialPresence) assetsFactor += 0.15;
    if (values.hasEmailList) assetsFactor += 0.25;
    
    // Calculate estimated value based on revenue and profit
    let estimatedValue = 0;
    
    if (monthlyProfit > 0) {
      // If profit data is available, prioritize it
      estimatedValue = monthlyProfit * profitMultiplier * growthFactor * assetsFactor;
    } else if (monthlyRevenue > 0) {
      // Otherwise use revenue
      estimatedValue = monthlyRevenue * revenueMultiplier * growthFactor * assetsFactor;
    } else {
      // If no financial data, estimate based on traffic and age
      estimatedValue = (trafficValue + ageValue) * industryFactor * growthFactor * assetsFactor;
    }
    
    // Calculate value range
    const valueRange = {
      min: estimatedValue * 0.8,
      max: estimatedValue * 1.2
    };
    
    // Calculate monthly multiple (how many months of profit the value represents)
    const monthlyMultiple = monthlyProfit > 0 ? estimatedValue / monthlyProfit : 0;
    
    return {
      revenueMultiplier,
      profitMultiplier,
      trafficValue,
      ageValue,
      industryFactor,
      growthFactor,
      assetsFactor,
      estimatedValue,
      valueRange,
      monthlyMultiple
    };
  };

  const handleInputChange = (field: keyof WebsiteValueState, value: string | boolean) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    const results = calculateWebsiteValue();
    setResults(results);
  }, [values]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('hr-HR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const industryNames = {
    ecommerce: 'E-trgovina',
    blog: 'Blog',
    saas: 'SaaS (Software as a Service)',
    media: 'Mediji i sadržaj',
    services: 'Usluge',
    other: 'Ostalo'
  };

  const growthRateNames = {
    declining: 'U padu',
    stable: 'Stabilno',
    growing: 'U rastu',
    rapid: 'Brzi rast'
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Vrijednosti Stranice",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte procijenjenu vrijednost vaše web stranice ili online biznisa. Uključuje faktore poput prihoda, prometa, starosti i industrije.",
    "url": "https://online-kalkulator.com/kalkulator-vrijednosti-stranice",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Procjena vrijednosti web stranice",
      "Izračun na temelju više faktora",
      "Raspon vrijednosti",
      "Analiza po industriji",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Kalkulator Vrijednosti Web Stranice | Izračunaj Vrijednost Poslovanja"
        description="Izračunajte procijenjenu vrijednost vaše web stranice ili online biznisa. Uključuje faktore poput prihoda, prometa, starosti i industrije."
        canonicalUrl="/kalkulator-vrijednosti-stranice"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Globe className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Vrijednosti Stranice</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Financial Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mjesečni prihod (€)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={values.monthlyRevenue}
                      onChange={(e) => handleInputChange('monthlyRevenue', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="npr. 5000"
                    />
                    <span className="absolute right-3 top-2 text-gray-500">€</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mjesečna dobit (€)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={values.monthlyProfit}
                      onChange={(e) => handleInputChange('monthlyProfit', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="npr. 2000"
                    />
                    <span className="absolute right-3 top-2 text-gray-500">€</span>
                  </div>
                </div>
              </div>

              {/* Traffic and Age */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mjesečni posjetitelji
                  </label>
                  <input
                    type="number"
                    value={values.monthlyVisitors}
                    onChange={(e) => handleInputChange('monthlyVisitors', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 10000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Starost stranice (godine)
                  </label>
                  <input
                    type="number"
                    value={values.websiteAge}
                    onChange={(e) => handleInputChange('websiteAge', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 3"
                  />
                </div>
              </div>

              {/* Industry and Growth */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Industrija
                  </label>
                  <select
                    value={values.industry}
                    onChange={(e) => handleInputChange('industry', e.target.value as WebsiteValueState['industry'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="ecommerce">E-trgovina</option>
                    <option value="blog">Blog</option>
                    <option value="saas">SaaS (Software as a Service)</option>
                    <option value="media">Mediji i sadržaj</option>
                    <option value="services">Usluge</option>
                    <option value="other">Ostalo</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stopa rasta
                  </label>
                  <select
                    value={values.growthRate}
                    onChange={(e) => handleInputChange('growthRate', e.target.value as WebsiteValueState['growthRate'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="declining">U padu</option>
                    <option value="stable">Stabilno</option>
                    <option value="growing">U rastu</option>
                    <option value="rapid">Brzi rast</option>
                  </select>
                </div>
              </div>

              {/* Additional Assets */}
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={values.hasCustomDomain}
                    onChange={(e) => handleInputChange('hasCustomDomain', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Vlastita domena</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={values.hasSocialPresence}
                    onChange={(e) => handleInputChange('hasSocialPresence', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Jaka prisutnost na društvenim mrežama</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={values.hasEmailList}
                    onChange={(e) => handleInputChange('hasEmailList', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Email lista pretplatnika</span>
                </label>
              </div>

              {/* Results */}
              {results && (
                <div className="space-y-4 border-t pt-4">
                  <div className="text-center py-3 px-4 rounded-lg bg-blue-50">
                    <p className="text-sm text-gray-600 mb-1">Procijenjena vrijednost stranice:</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatCurrency(results.estimatedValue)}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Raspon: {formatCurrency(results.valueRange.min)} - {formatCurrency(results.valueRange.max)}
                    </p>
                  </div>

                  {results.monthlyMultiple > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Višekratnik mjesečne dobiti:</span>
                      <span className="font-semibold">
                        {results.monthlyMultiple.toFixed(1)}x
                      </span>
                    </div>
                  )}

                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-gray-700">Faktori koji utječu na vrijednost:</h3>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Industrija ({industryNames[values.industry]}):</span>
                      <span className="font-medium">{industryMultipliers[values.industry].min}-{industryMultipliers[values.industry].max}x mjesečno</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Stopa rasta ({growthRateNames[values.growthRate]}):</span>
                      <span className="font-medium">{growthFactors[values.growthRate]}x</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Dodatna imovina:</span>
                      <span className="font-medium">{results.assetsFactor.toFixed(2)}x</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Vrijednosti Stranice</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator vrijednosti stranice je alat koji vam pomaže procijeniti približnu tržišnu
                  vrijednost vaše web stranice ili online biznisa. Uzima u obzir različite faktore
                  poput prihoda, dobiti, prometa, starosti, industrije i stope rasta kako bi pružio
                  realističnu procjenu vrijednosti.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako se Izračunava Vrijednost</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <DollarSign className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-lg font-medium text-gray-700 mb-1">Financijski pokazatelji</h3>
                      <p className="text-gray-600">
                        Najvažniji faktor u procjeni vrijednosti je financijska izvedba. Web stranice se
                        obično vrednuju kao višekratnik mjesečne dobiti (tipično 24-48x za stabilne biznise).
                        Što je veća dobit, to je veća vrijednost.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Users className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-lg font-medium text-gray-700 mb-1">Promet i publika</h3>
                      <p className="text-gray-600">
                        Broj mjesečnih posjetitelja utječe na vrijednost, posebno za stranice koje još
                        nemaju značajne prihode. Kvaliteta prometa (stopa konverzije, vrijeme na stranici)
                        također je važna.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Clock className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-lg font-medium text-gray-700 mb-1">Starost i povijest</h3>
                      <p className="text-gray-600">
                        Starije web stranice s dokazanom poviješću obično vrijede više. Stranice starije
                        od 2-3 godine smatraju se stabilnijima i manje rizičnima.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <BarChart className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-lg font-medium text-gray-700 mb-1">Industrija i niša</h3>
                      <p className="text-gray-600">
                        Različite industrije imaju različite višekratnike. SaaS biznisi obično imaju
                        najviše višekratnike, dok generički sadržajni sajtovi imaju niže.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <TrendingUp className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-lg font-medium text-gray-700 mb-1">Stopa rasta</h3>
                      <p className="text-gray-600">
                        Stranice koje pokazuju konzistentan rast vrijede više od onih sa stabilnim ili
                        padajućim trendovima. Brzorastuće stranice mogu postići višekratnike i do 50-60x
                        mjesečne dobiti.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Tipični Višekratnici po Industriji</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Industrija
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tipični raspon
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Napomene
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">E-trgovina</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">24-36x mjesečno</td>
                        <td className="px-6 py-4 text-sm text-gray-500">Viši za vlastite proizvode, niži za dropshipping</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">SaaS</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">36-48x mjesečno</td>
                        <td className="px-6 py-4 text-sm text-gray-500">Najviši višekratnici zbog predvidljivih prihoda</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Blog/Sadržaj</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">24-36x mjesečno</td>
                        <td className="px-6 py-4 text-sm text-gray-500">Ovisi o kvaliteti sadržaja i diverzifikaciji prihoda</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Mediji</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">24-36x mjesečno</td>
                        <td className="px-6 py-4 text-sm text-gray-500">Viši za etablirane brendove s lojalnom publikom</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Usluge</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">24-36x mjesečno</td>
                        <td className="px-6 py-4 text-sm text-gray-500">Ovisi o automatizaciji i ovisnosti o vlasniku</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite mjesečni prihod i dobit vaše web stranice</li>
                  <li>Unesite mjesečni broj posjetitelja</li>
                  <li>Unesite starost stranice u godinama</li>
                  <li>Odaberite industriju koja najbolje opisuje vašu stranicu</li>
                  <li>Odaberite trenutnu stopu rasta</li>
                  <li>Označite dodatne prednosti koje vaša stranica ima</li>
                  <li>Rezultati će se automatski izračunati</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Povećati Vrijednost Web Stranice</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Povećajte i diverzificirajte izvore prihoda</li>
                  <li>Smanjite operativne troškove za veću dobit</li>
                  <li>Povećajte promet kroz SEO i marketing</li>
                  <li>Poboljšajte stope konverzije i zadržavanja korisnika</li>
                  <li>Automatizirajte procese kako bi stranica zahtijevala manje upravljanja</li>
                  <li>Izgradite email listu i prisutnost na društvenim mrežama</li>
                  <li>Dokumentirajte procese i sustave za lakši prijenos</li>
                  <li>Razvijte jedinstvenu prednost ili intelektualno vlasništvo</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Izračuni su informativne prirode</li>
                  <li>Stvarna prodajna cijena može značajno varirati</li>
                  <li>Tržišni uvjeti mogu utjecati na vrijednost</li>
                  <li>Kvalitativni faktori poput brenda i reputacije također utječu na vrijednost</li>
                  <li>Za preciznu procjenu, konzultirajte stručnjaka za procjenu web biznisa</li>
                  <li>Vrijednost se može razlikovati ovisno o kupcu i njihovim ciljevima</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Proces Prodaje Web Stranice</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Priprema financijskih izvještaja i analitike</li>
                  <li>Dokumentiranje svih procesa i sustava</li>
                  <li>Procjena vrijednosti (koristite ovaj kalkulator kao početnu točku)</li>
                  <li>Odluka o načinu prodaje (direktno ili preko brokera)</li>
                  <li>Izrada prospekta ili memoranduma o prodaji</li>
                  <li>Oglašavanje prodaje na odgovarajućim platformama</li>
                  <li>Pregovori s potencijalnim kupcima</li>
                  <li>Due diligence proces</li>
                  <li>Finalizacija prodaje i prijenos vlasništva</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Gdje Prodati Web Stranicu</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Specijalizirane platforme za prodaju web biznisa (Flippa, Empire Flippers, FE International)</li>
                  <li>Forumi i grupe za kupoprodaju web stranica</li>
                  <li>Direktno kontaktiranje potencijalnih kupaca u vašoj niši</li>
                  <li>Angažiranje brokera za web biznise (za stranice veće vrijednosti)</li>
                  <li>Networking na konferencijama i događajima vezanim za web industriju</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorVrijednostiStranice;